import { cn } from "@/lib/utils";
import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { createPortal } from "react-dom";

interface SlideOverPanelProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  className?: string;
}

export function SlideOverPanel({ open, onClose, title, children, className }: SlideOverPanelProps) {
  const [rendered, setRendered] = useState(open);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (open) {
      setVisible(false);
      setRendered(true);
      return;
    }

    setVisible(false);
    if (!rendered) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setRendered(false);
    }, 300);

    return () => window.clearTimeout(timeout);
  }, [open, rendered]);

  useEffect(() => {
    if (!rendered || !open) return;
    const frame = window.requestAnimationFrame(() => {
      setVisible(true);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [rendered, open]);

  useEffect(() => {
    if (!rendered) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [rendered, onClose]);

  if (!rendered) return null;

  return createPortal(
    <>
      <div
        className={cn(
          "fixed inset-0 z-40 bg-foreground/20 transition-opacity duration-300",
          visible ? "opacity-100" : "opacity-0",
        )}
        onClick={onClose}
      />
      <div className={cn(
        "fixed inset-y-0 right-0 z-50 flex h-screen min-h-screen w-full flex-col border-l bg-card shadow-xl transition-transform duration-300 ease-out sm:w-[420px]",
        visible ? "translate-x-0" : "translate-x-full",
        className
      )}>
        <div className="flex items-center justify-between px-5 py-4 border-b">
          <h2 className="font-semibold text-[#0C1F4F]">{title}</h2>
          <button onClick={onClose} className="p-1.5 rounded-md hover:bg-secondary transition-colors">
            <X className="h-4 w-4 text-muted-foreground" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5">
          {children}
        </div>
      </div>
    </>,
    document.body,
  );
}
